// templateSelectors.ts
import { get as lodashGet } from "lodash";
import * as types from "_graphql-types-frontend/graphql";
import { Field, Metadata, TemplateStore } from "../types";
import { getFieldValueFromData } from "./configHelpers";

const nullMetadata: Metadata = {
  displayType: "none",
  displayName: "none",
  value: {
    returnTypeName: "none",
    propertyKey: "none",
    isScalar: false,
    nullable: false,
    arrayDepth: 0,
    props: [],
  },
};

// First, import createSelector from reselect
import { createSelector } from "reselect";

// Create base selectors that extract pieces of state
const getTemplateData = (state: TemplateStore) => state.templateData;
const getPublishedFieldData = (state: TemplateStore) =>
  state.publishedFieldData?.data;
const getManagerFieldData = (state: TemplateStore) =>
  state.managerFieldData?.data;
const getRangerFieldData = (state: TemplateStore) =>
  state.rangerFieldData?.data;
const getEditFieldData = (state: TemplateStore) => state.editFieldData?.data;
const getPersistingFields = (state: TemplateStore) => state.persistingFields;

// Create memoized selectors using createSelector
export const selectFieldFromCacheMemoized = createSelector(
  [
    getTemplateData,
    getPublishedFieldData,
    getManagerFieldData,
    getRangerFieldData,
    (_, key) => key,
    (_, __, state) => state,
    (_, __, ___, currentUser) => currentUser?.flags.subscriberDataAccess,
  ],
  (
    templateData,
    publishedData,
    managerData,
    rangerData,
    key,
    state,
    hasDataAccess
  ) => {
    if (!templateData || !publishedData || !managerData) {
      return {
        field: null,
        metadata: nullMetadata,
      };
    }

    const [fieldOwnerType, fieldKey] = key.split(".");
    const metadata = lodashGet(templateData.fieldMeta, [
      fieldOwnerType,
      fieldKey,
    ]);

    if (!metadata) {
      return {
        field: null,
        metadata: nullMetadata,
      };
    }

    let field = null;

    if (state !== undefined) {
      if (state === null) {
        field = getFieldValueFromData(publishedData, fieldOwnerType, fieldKey);
      } else if (state === types.FieldState.Mgr) {
        field = getFieldValueFromData(managerData, fieldOwnerType, fieldKey);
      } else if (state === types.FieldState.RangerGenerated) {
        field = getFieldValueFromData(rangerData, fieldOwnerType, fieldKey);
      }
    } else {
      if (hasDataAccess) {
        field = getFieldValueFromData(publishedData, fieldOwnerType, fieldKey);
      }
      if (!field) {
        field = getFieldValueFromData(managerData, fieldOwnerType, fieldKey);
      }
    }

    return {
      field: field || null,
      metadata,
    };
  }
);

// Similar memoized selectors for other functions
export const selectDraftFieldFromCacheMemoized = createSelector(
  [getTemplateData, getEditFieldData, (_, key) => key],
  (templateData, editData, key) => {
    if (!templateData || !editData) {
      return {
        field: null,
        metadata: nullMetadata,
      };
    }

    const [fieldOwnerType, fieldKey] = key.split(".");
    const metadata = lodashGet(templateData.fieldMeta, [
      fieldOwnerType,
      fieldKey,
    ]);

    if (!metadata) {
      return {
        field: null,
        metadata: nullMetadata,
      };
    }

    const field = getFieldValueFromData(editData, fieldOwnerType, fieldKey);

    return {
      field: field || null,
      metadata,
    };
  }
);

export const selectIsPersistingFieldMemoized = createSelector(
  [getPersistingFields, (_, field) => field],
  (persistingFields, field) => {
    if (!field) return false;
    const key = `${field.ownerId}::${field?.ownerType}::${field.fieldKey}`;
    const requestList = persistingFields[key];
    return requestList ? requestList.length > 0 : false;
  }
);

export function selectMetadata(
  state: { templateData?: { fieldMeta?: any } },
  fieldKey: string
) {
  const templateData = state.templateData;

  if (!templateData) {
    return nullMetadata;
  }

  const [fieldOwnerType, actualFieldKey] = fieldKey.includes(".")
    ? fieldKey.split(".")
    : ["investment", fieldKey];

  const metadata = lodashGet(
    templateData,
    `fieldMeta.${fieldOwnerType}.${actualFieldKey}`
  );

  return metadata || nullMetadata;
}
